import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { apiService } from "../../services/api";
import { buildLookupPayload } from "../../utils/lookupUtils";
import {
  sumBatchAllocationsByBatchId,
  getCrossRowBatchConflict,
  type BatchAllocationSum,
} from "../../utils/batchAllocationUtils";

export interface BatchItem {
  id: string;
  batch_no: string;
  purchase_no?: string;
  vendor_name?: string;
  available_qty: number;
  rate: number;
  expiry_date?: string;
}

export interface AllocationEntry {
  batch_id: string;
  batch_no: string;
  qty: number;
  rate: number;
  total: number;
  available_qty?: number;
}

export interface BatchAllocationFieldProps {
  config: {
    name: string;
    label?: string;
    /** When set, batches are fetched from API (issue mode). When absent, batches come from value prop (return mode). */
    lookupName?: string;
    parentField?: string;
    issueQtyField?: string;
    rateField?: string;
    /** Label for the qty column in the modal (default: auto-derived from mode) */
    qtyLabel?: string;
    /** Lookup used in source mode to enrich batch rows with vendor/purchase metadata */
    metadataLookupName?: string;
    /** Row field holding product id for metadata enrichment (return flow uses product_id, not drp_code) */
    metadataParentField?: string;
    Json_Attribute?: string;
    BindValue?: string;
    disabled?: boolean;
    [key: string]: any;
  };
  value: any;
  onChange: (name: string, value: any, allocationTotal?: number) => void;
  rowData: Record<string, any>;
  rowIndex: number;
  formData: Record<string, any>;
  allRows?: Record<string, any>[];
  isReadOnly?: boolean;
}

function autoAllocateFIFO(
  batches: BatchItem[],
  issueQty: number,
  usedByOtherRows: Record<string, BatchAllocationSum> = {}
): AllocationEntry[] {
  const allocations: AllocationEntry[] = [];
  let remaining = issueQty;

  for (const batch of batches) {
    if (remaining <= 0) break;
    const alreadyUsed = usedByOtherRows[batch.id]?.qty ?? 0;
    const effectiveAvailable = Math.max(0, batch.available_qty - alreadyUsed);
    const allocQty = Math.min(remaining, effectiveAvailable);
    if (allocQty > 0) {
      allocations.push({
        batch_id: batch.id,
        batch_no: batch.batch_no,
        qty: allocQty,
        rate: batch.rate,
        total: allocQty * batch.rate,
        available_qty: batch.available_qty,
      });
      remaining -= allocQty;
    }
  }

  return allocations;
}

function formatNumber(num: number): string {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function qtysEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.001;
}

function attachAvailableQty(
  entries: AllocationEntry[],
  batches: BatchItem[]
): AllocationEntry[] {
  return entries.map((entry) => {
    if (entry.available_qty !== undefined) return entry;
    const batch = batches.find((b) => b.id === entry.batch_id);
    return batch ? { ...entry, available_qty: batch.available_qty } : entry;
  });
}

function mapApiItemToBatchItem(item: any): BatchItem {
  return {
    id: item.id || item.batch_id,
    batch_no:
      item.batch_no ?? item.batchno ?? item.name ?? item.label ?? "",
    purchase_no: item.purchase_no ?? item.purchase_id ?? "",
    vendor_name: item.suppliername ?? item.vendor_name ?? "",
    available_qty: parseFloat(
      item.available_qty ?? item.qty ?? item.stock_qty ?? "0"
    ),
    rate: parseFloat(
      item.rate ?? item.purchase_rate ?? item.cost_rate ?? "0"
    ),
    expiry_date: item.expiry_date,
  };
}

function mapAllocationToBatchItem(item: any): BatchItem {
  return {
    id: item.batch_id || item.id,
    batch_no: item.batch_no ?? item.batchno ?? "",
    purchase_no: item.purchase_no ?? item.purchase_id ?? "",
    vendor_name: item.vendor_name ?? item.suppliername ?? "",
    available_qty: parseFloat(item.qty ?? item.available_qty ?? "0"),
    rate: parseFloat(item.rate ?? item.purchase_rate ?? "0"),
    expiry_date: item.expiry_date,
  };
}

// ─── Modal rendered via portal ────────────────────────────────────────────────

interface BatchModalProps {
  batches: BatchItem[];
  allocations: AllocationEntry[];
  issueQty: number;
  otherRowsUsage: Record<string, BatchAllocationSum>;
  title?: string;
  qtyLabel?: string;
  availableLabel?: string;
  /** Overrides the default allocation mismatch banner (e.g. return qty vs issued qty). */
  mismatchMessage?: string;
  onQtyChange: (batchId: string, qty: number) => void;
  onSave: () => void;
  onClose: () => void;
}

const BatchModal: React.FC<BatchModalProps> = ({
  batches,
  allocations,
  issueQty,
  otherRowsUsage,
  title = "Issue Batch Allocation",
  qtyLabel = "Issue Qty",
  availableLabel = "Available",
  mismatchMessage,
  onQtyChange,
  onSave,
  onClose,
}) => {
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const totalAllocated = allocations.reduce((s, a) => s + a.qty, 0);
  const grandTotal = allocations.reduce((s, a) => s + a.total, 0);
  const allocationMatchesIssueQty =
    issueQty <= 0 ? totalAllocated <= 0 : qtysEqual(totalAllocated, issueQty);
  const overAllocated = totalAllocated > issueQty;
  const remaining = issueQty - totalAllocated;

  const crossRowWarnings = batches
    .map((batch) => {
      const usedElsewhere = otherRowsUsage[batch.id]?.qty ?? 0;
      if (usedElsewhere <= 0) return null;
      return `${usedElsewhere} already allocated in other rows for ${batch.batch_no}`;
    })
    .filter(Boolean);

  return ReactDOM.createPortal(
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-primary-600 dark:bg-primary-700 text-white">
          <div>
            <h3 className="text-base font-semibold">{title}</h3>
            <span className="text-xs opacity-80">
              {qtyLabel}: {issueQty} &nbsp;|&nbsp; Allocated: {totalAllocated}
              {remaining > 0 && <> &nbsp;|&nbsp; Remaining: {remaining}</>}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/20 transition-colors"
            aria-label="Close"
          >
            <svg
              width={18}
              height={18}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {crossRowWarnings.length > 0 && (
          <div className="mx-5 mt-4 px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-sm">
            {crossRowWarnings.join(" · ")}
          </div>
        )}

        {/* Allocation mismatch warning */}
        {!allocationMatchesIssueQty && issueQty > 0 && (
          <div className="mx-5 mt-4 px-3 py-2 rounded bg-danger-50 dark:bg-danger-900/30 border border-danger-300 dark:border-danger-700 text-danger-700 dark:text-danger-400 text-sm flex items-center gap-2">
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            {mismatchMessage
              ? mismatchMessage
              : overAllocated
                ? `Total allocated qty (${totalAllocated}) exceeds ${qtyLabel.toLowerCase()} (${issueQty})`
                : `Total allocated qty (${totalAllocated}) must equal ${qtyLabel.toLowerCase()} (${issueQty})`}
          </div>
        )}

        {/* Table */}
        <div className="p-5">
          <table className="w-full border border-gray-200 dark:border-gray-700 text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-left">
                <th className="px-4 py-2.5 font-semibold border-b border-gray-200 dark:border-gray-600">
                  Batch
                </th>
                <th className="px-4 py-2.5 font-semibold border-b border-gray-200 dark:border-gray-600">
                  Purchase ID
                </th>
                <th className="px-4 py-2.5 font-semibold border-b border-gray-200 dark:border-gray-600">
                  Vendor
                </th>
                <th className="px-4 py-2.5 font-semibold border-b border-gray-200 dark:border-gray-600">
                  {availableLabel}
                </th>
                <th className="px-4 py-2.5 font-semibold border-b border-gray-200 dark:border-gray-600">
                  Rate
                </th>
                <th className="px-4 py-2.5 font-semibold border-b border-gray-200 dark:border-gray-600">
                  {qtyLabel}
                </th>
                <th className="px-4 py-2.5 font-semibold border-b border-gray-200 dark:border-gray-600 text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => {
                const alloc = allocations.find(
                  (a) => a.batch_id === batch.id
                );
                const qty = alloc?.qty ?? 0;
                const total = qty * batch.rate;

                const othersTotal = allocations
                  .filter((a) => a.batch_id !== batch.id)
                  .reduce((s, a) => s + a.qty, 0);
                const usedElsewhere = otherRowsUsage[batch.id]?.qty ?? 0;
                const effectiveAvailable = Math.max(
                  0,
                  batch.available_qty - usedElsewhere
                );
                const maxForThisBatch = Math.min(
                  effectiveAvailable,
                  issueQty - othersTotal
                );

                return (
                  <tr
                    key={batch.id}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750"
                  >
                    <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">
                      {batch.batch_no}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">
                      {batch.purchase_no || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">
                      {batch.vendor_name || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">
                      {effectiveAvailable}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">
                      {formatNumber(batch.rate)}
                    </td>
                    <td className="px-4 py-2.5">
                      <input
                        type="number"
                        min={0}
                        max={Math.max(maxForThisBatch, 0)}
                        value={qty}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          onQtyChange(batch.id, isNaN(v) ? 0 : v);
                        }}
                        className={`w-full px-3 py-1.5 border rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          qty > maxForThisBatch
                            ? "border-danger-400 dark:border-danger-500"
                            : "border-gray-300 dark:border-gray-600"
                        }`}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-800 dark:text-gray-200">
                      {formatNumber(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className={`font-semibold text-sm ${!allocationMatchesIssueQty && issueQty > 0 ? "bg-danger-50 dark:bg-danger-900/20" : "bg-gray-50 dark:bg-gray-700"}`}>
                <td className={`px-4 py-2 ${!allocationMatchesIssueQty && issueQty > 0 ? "text-danger-700 dark:text-danger-400" : "text-gray-700 dark:text-gray-300"}`}>
                  Total
                </td>
                <td className="px-4 py-2" />
                <td className="px-4 py-2" />
                <td className="px-4 py-2" />
                <td className="px-4 py-2" />
                <td className={`px-4 py-2 ${!allocationMatchesIssueQty && issueQty > 0 ? "text-danger-700 dark:text-danger-400" : "text-gray-700 dark:text-gray-300"}`}>
                  {totalAllocated} / {issueQty}
                </td>
                <td className={`px-4 py-2 text-right ${!allocationMatchesIssueQty && issueQty > 0 ? "text-danger-700 dark:text-danger-400" : "text-gray-700 dark:text-gray-300"}`}>
                  {formatNumber(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!allocationMatchesIssueQty}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
              !allocationMatchesIssueQty
                ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                : "bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
            }`}
          >
            Save Allocation
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const BatchAllocationField: React.FC<BatchAllocationFieldProps> = ({
  config,
  value,
  onChange,
  rowData,
  rowIndex,
  formData,
  allRows = [],
  isReadOnly = false,
}) => {
  const [batches, setBatches] = useState<BatchItem[]>([]);
  const [allocations, setAllocations] = useState<AllocationEntry[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAllocations, setModalAllocations] = useState<AllocationEntry[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const prevProductRef = useRef<string | null>(null);
  const prevFifoKeyRef = useRef<string>("");
  const batchesRef = useRef<BatchItem[]>([]);
  const metadataEnrichedRef = useRef<string | null>(null);

  const fieldName = config.name || config.BindValue || "batch_allocation";
  const parentField = config.parentField;
  const metadataParentField = config.metadataParentField;
  const issueQtyField = config.issueQtyField || "txt_issued_qty";
  /** No lookupName → batches are sourced from the value prop (return flow) */
  const isSourceMode = !config.lookupName;

  const otherRowsUsage = useMemo(() => {
    if (isSourceMode) return {};
    return sumBatchAllocationsByBatchId(allRows, {
      fieldName,
      excludeRowIndex: rowIndex,
    });
  }, [isSourceMode, allRows, fieldName, rowIndex]);
  const otherRowsUsageKey = useMemo(
    () => JSON.stringify(otherRowsUsage),
    [otherRowsUsage]
  );

  const resolveRowField = (field?: string) => {
    if (!field) return null;
    return (
      rowData[field] ??
      rowData[field.replace?.(/^drp_/, "") ?? ""] ??
      null
    );
  };

  const productCode = resolveRowField(parentField);
  const metadataProductId = resolveRowField(metadataParentField) ?? productCode;

  const issueQty =
    parseFloat(
      rowData[issueQtyField] ?? rowData.issued_qty ?? rowData.return_qty ?? "0"
    ) || 0;

  /** Issued/available qty on the line (parts return: txt_qty). */
  const lineIssuedQty =
    parseFloat(rowData.txt_qty ?? rowData.qty ?? rowData.issued_qty ?? "0") ||
    0;

  const qtyLabel =
    config.qtyLabel || (isSourceMode ? "Return Qty" : "Issue Qty");

  const pushAllocations = useCallback(
    (newAllocs: AllocationEntry[]) => {
      const withAvailable = attachAvailableQty(newAllocs, batchesRef.current);
      setAllocations(withAvailable);

      const totalAmt = withAvailable.reduce((s, a) => s + a.total, 0);
      onChange(fieldName, withAvailable, totalAmt);
    },
    [fieldName, onChange]
  );

  // ─── Source mode: read batches from value prop (e.g. return flow) ───
  // The initial value contains the issued batch data from the lookup.
  // We parse it ONCE to build the available batches list.
  const sourceInitializedRef = useRef(false);

  useEffect(() => {
    if (!isSourceMode) return;
    if (sourceInitializedRef.current) return;
    if (!value) return;

    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      if (!Array.isArray(parsed) || parsed.length === 0) return;

      sourceInitializedRef.current = true;

      const items: BatchItem[] = parsed.map(mapAllocationToBatchItem);

      setBatches(items);
      batchesRef.current = items;

      if (issueQty > 0) {
        pushAllocations(autoAllocateFIFO(items, issueQty, otherRowsUsage));
      }
    } catch {
      /* ignore parse errors */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceMode, value]);

  // ─── Source mode: enrich vendor/purchase metadata from API ─────────
  useEffect(() => {
    if (!isSourceMode) return;
    const lookupName = config.metadataLookupName;
    if (!lookupName || !metadataProductId) return;
    if (batchesRef.current.length === 0) return;

    const enrichKey = `${metadataProductId}:${batchesRef.current.map((b) => b.id).join(",")}`;
    if (metadataEnrichedRef.current === enrichKey) return;

    let cancelled = false;
    const enrichBatches = async () => {
      try {
        const payload = buildLookupPayload({
          parentid: metadataProductId,
          lookuptype: "Select",
        });
        const raw = await apiService.getRawLookupData(lookupName, payload);
        if (cancelled) return;

        const metaById = new Map<
          string,
          { vendor_name?: string; purchase_no?: string }
        >();
        const metaByBatchNo = new Map<
          string,
          { vendor_name?: string; purchase_no?: string }
        >();
        for (const item of raw) {
          const meta = {
            vendor_name: item.suppliername ?? item.vendor_name ?? "",
            purchase_no: item.purchase_no ?? item.purchase_id ?? "",
          };
          const id = String(item.id || item.batch_id);
          metaById.set(id, meta);
          const batchNo =
            item.batch_no ?? item.batchno ?? item.name ?? item.label ?? "";
          if (batchNo) metaByBatchNo.set(String(batchNo), meta);
        }

        let matched = 0;
        const enriched = batchesRef.current.map((batch) => {
          const meta =
            metaById.get(String(batch.id)) ??
            metaByBatchNo.get(String(batch.batch_no));
          if (!meta) return batch;
          matched += 1;
          return {
            ...batch,
            vendor_name: meta.vendor_name || batch.vendor_name,
            purchase_no: meta.purchase_no || batch.purchase_no,
          };
        });

        if (matched > 0) {
          metadataEnrichedRef.current = enrichKey;
          setBatches(enriched);
          batchesRef.current = enriched;
        }
      } catch (err) {
        console.error("[BatchAllocation] Failed to enrich batch metadata:", err);
      }
    };

    enrichBatches();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceMode, metadataProductId, config.metadataLookupName, batches.length]);

  // ─── API mode: restore allocations from value (issue flow) ─────────
  useEffect(() => {
    if (isSourceMode) return;
    if (!value) return;
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      if (Array.isArray(parsed) && parsed.length > 0) {
        setAllocations(attachAvailableQty(parsed, batchesRef.current));
      }
    } catch {
      /* ignore parse errors */
    }
  }, [value, isSourceMode]);

  // ─── API mode: fetch batches when product changes ───────────────────
  useEffect(() => {
    if (isSourceMode) return;
    if (!productCode || !config.lookupName) return;
    if (prevProductRef.current === String(productCode)) return;
    prevProductRef.current = String(productCode);

    let cancelled = false;
    const fetchBatches = async () => {
      setLoading(true);
      try {
        const payload = buildLookupPayload({
          parentid: productCode,
          lookuptype: "Select",
        });
        const raw = await apiService.getRawLookupData(
          config.lookupName!,
          payload
        );
        if (cancelled) return;

        const items: BatchItem[] = raw.map(mapApiItemToBatchItem);

        setBatches(items);
        batchesRef.current = items;

        if (issueQty > 0) {
          pushAllocations(autoAllocateFIFO(items, issueQty, otherRowsUsage));
        }
      } catch (err) {
        console.error("[BatchAllocation] Failed to fetch batches:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBatches();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSourceMode, productCode, config.lookupName]);

  // Re-allocate when issue qty or sibling row usage changes
  useEffect(() => {
    if (batchesRef.current.length === 0) return;
    const fifoKey = `${issueQty}:${otherRowsUsageKey}`;
    if (prevFifoKeyRef.current === fifoKey) return;
    prevFifoKeyRef.current = fifoKey;

    if (issueQty > 0) {
      pushAllocations(
        autoAllocateFIFO(batchesRef.current, issueQty, otherRowsUsage)
      );
    } else {
      pushAllocations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueQty, otherRowsUsageKey]);

  // ─── Modal handlers ─────────────────────────────────────────────────

  const openModal = useCallback(() => {
    const merged: AllocationEntry[] = batches.map((b) => {
      const existing = allocations.find((a) => a.batch_id === b.id);
      return {
        batch_id: b.id,
        batch_no: b.batch_no,
        qty: existing?.qty ?? 0,
        rate: b.rate,
        total: (existing?.qty ?? 0) * b.rate,
        available_qty: b.available_qty,
      };
    });
    setModalAllocations(merged);
    setModalOpen(true);
  }, [batches, allocations]);

  const handleModalQtyChange = useCallback(
    (batchId: string, newQty: number) => {
      const batch = batches.find((b) => b.id === batchId);
      if (!batch) return;

      setModalAllocations((prev) => {
        const othersTotal = prev
          .filter((a) => a.batch_id !== batchId)
          .reduce((s, a) => s + a.qty, 0);
        const usedElsewhere = otherRowsUsage[batchId]?.qty ?? 0;
        const effectiveAvailable = Math.max(
          0,
          batch.available_qty - usedElsewhere
        );
        const maxAllowed = Math.min(effectiveAvailable, issueQty - othersTotal);
        const clamped = Math.max(0, Math.min(newQty, maxAllowed));

        return prev.map((a) =>
          a.batch_id === batchId
            ? {
                ...a,
                qty: clamped,
                total: clamped * a.rate,
                available_qty: batch.available_qty,
              }
            : a
        );
      });
    },
    [batches, issueQty, otherRowsUsage]
  );

  const handleSaveAllocation = useCallback(() => {
    const totalAllocated = modalAllocations.reduce((s, a) => s + a.qty, 0);
    if (issueQty > 0 && !qtysEqual(totalAllocated, issueQty)) {
      return;
    }
    const nonZero = modalAllocations
      .filter((a) => a.qty > 0)
      .map((a) => {
        const batch = batches.find((b) => b.id === a.batch_id);
        return {
          ...a,
          available_qty: batch?.available_qty ?? a.available_qty,
        };
      });
    pushAllocations(nonZero);
    setModalOpen(false);
  }, [modalAllocations, pushAllocations, issueQty, batches]);

  // ─── Render compact view ────────────────────────────────────────────

  const totalAllocated = allocations.reduce((s, a) => s + a.qty, 0);
  const allocationMismatch =
    issueQty > 0 && !qtysEqual(totalAllocated, issueQty);
  /** Parts return: return qty exceeding issued qty — clearer than allocated vs return. */
  const returnExceedsIssued =
    isSourceMode && issueQty > 0 && issueQty > lineIssuedQty;
  const allocationMismatchMessage = returnExceedsIssued
    ? `return qty (${issueQty}) must be equal to Issued qty (${lineIssuedQty})`
    : `Allocated (${totalAllocated}) must equal ${qtyLabel.toLowerCase()} (${issueQty})`;

  const crossRowConflicts = allocations
    .map((a) => {
      const available =
        a.available_qty ??
        batches.find((b) => b.id === a.batch_id)?.available_qty ??
        0;
      return getCrossRowBatchConflict(
        a.batch_id,
        a.qty,
        otherRowsUsage,
        available,
        a.batch_no
      );
    })
    .filter(Boolean) as string[];

  if (loading) {
    return (
      <div className="text-xs text-gray-400 dark:text-gray-500 py-1 px-2">
        Loading batches…
      </div>
    );
  }

  if ((!isSourceMode && !productCode) || batches.length === 0) {
    return (
      <div className="text-xs text-gray-400 dark:text-gray-500 py-1 px-2">
        {!isSourceMode && !productCode ? "Select item first" : "No batches"}
      </div>
    );
  }

  return (
    <>
      <div className="relative border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-xs min-w-[180px]">
        {/* Compact header */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-gray-100 dark:border-gray-700">
          <span className="font-semibold text-gray-700 dark:text-gray-300 text-[11px] uppercase tracking-wide">
            {config.label || "Batch Allocation"}
          </span>
          {!isReadOnly && (
            <button
              type="button"
              onClick={openModal}
              className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              title="Edit allocation"
            >
              <svg
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
          )}
        </div>

        {/* FIFO allocation rows */}
        <div className="px-2 py-1 space-y-0.5">
          <div className="text-[10px] text-gray-400 dark:text-gray-500 font-medium mb-0.5">
            FIFO Allocation
          </div>
          {allocations.length === 0 ? (
            <div className="text-gray-400 dark:text-gray-500 italic">
              No allocation
            </div>
          ) : (
            allocations.map((a) => (
              <div
                key={a.batch_id}
                className="flex items-center justify-between gap-2"
              >
                <span className="font-semibold text-primary-700 dark:text-primary-400">
                  {a.batch_no}
                </span>
                <span className="text-gray-500 dark:text-gray-400 flex-1 text-center whitespace-nowrap">
                  {a.qty} &times; {formatNumber(a.rate)}
                </span>
                <span className="font-semibold text-primary-700 dark:text-primary-400 tabular-nums">
                  {formatNumber(a.total)}
                </span>
              </div>
            ))
          )}
        </div>
        {crossRowConflicts.map((msg) => (
          <div
            key={msg}
            className="px-2 py-1 text-[10px] text-danger-600 dark:text-danger-400 border-t border-danger-200 dark:border-danger-800"
          >
            {msg}
          </div>
        ))}
        {allocationMismatch && (
          <div className="px-2 py-1 text-[10px] text-danger-600 dark:text-danger-400 border-t border-danger-200 dark:border-danger-800">
            {allocationMismatchMessage}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <BatchModal
          batches={batches}
          allocations={modalAllocations}
          issueQty={issueQty}
          otherRowsUsage={otherRowsUsage}
          title={isSourceMode ? (config.label || "Return Batch Allocation") : "Issue Batch Allocation"}
          qtyLabel={qtyLabel}
          availableLabel={isSourceMode ? "Issued Qty" : "Available"}
          mismatchMessage={
            returnExceedsIssued ? allocationMismatchMessage : undefined
          }
          onQtyChange={handleModalQtyChange}
          onSave={handleSaveAllocation}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default BatchAllocationField;
